import React, { useState, useEffect } from 'react';
import { getClientConfigAsync, updateClientConfigAsync } from '../../../Services/AdminService';
import { showToast } from '../../../Infrastructure/Notifications/ToastrConfig';

const COLOR_FIELDS = [
    { name: 'primaryNavColour', label: 'Sidebar Background' },
    { name: 'sidebarIconColour', label: 'Sidebar Icons' },
    { name: 'secondaryNavColour', label: 'Top Navigation' },
    { name: 'secondaryNavFontColour', label: 'Nav Font' },
    { name: 'primaryButtonColour', label: 'Primary Buttons' },
    { name: 'primaryNavFontColour', label: 'Button Text' },
    { name: 'iconColour', label: 'Accent & Icons' },
    { name: 'secondaryButtonColour', label: 'Secondary Buttons' },
];

const DEFAULTS = {
    primaryNavColour: '#1e293b',
    secondaryNavColour: '#ffffff',
    primaryButtonColour: '#6366f1',
    secondaryButtonColour: '#e2e8f0',
    primaryNavFontColour: '#ffffff',
    secondaryNavFontColour: '#1e293b',
    iconColour: '#6366f1',
    sidebarIconColour: '#94a3b8',
};

// Read a property from API response regardless of casing
const pick = (obj, ...keys) => {
    for (const k of keys) {
        if (obj[k] != null && obj[k] !== '') return obj[k];
    }
    return null;
};

const mapApiConfigToState = (response) => {
    const source = response?.config || response || {};

    return {
        primaryNavColour: pick(source, 'primaryNavColour', 'PrimaryNavColour') || DEFAULTS.primaryNavColour,
        secondaryNavColour: pick(source, 'secondaryNavColour', 'SecondaryNavColour') || DEFAULTS.secondaryNavColour,
        primaryButtonColour: pick(source, 'primaryButtonColour', 'PrimaryButtonColour') || DEFAULTS.primaryButtonColour,
        secondaryButtonColour: pick(source, 'secondaryButtonColour', 'SecondaryButtonColour') || DEFAULTS.secondaryButtonColour,
        primaryNavFontColour: pick(source, 'primaryNavFontColour', 'PrimaryNavFontColour') || DEFAULTS.primaryNavFontColour,
        secondaryNavFontColour: pick(source, 'secondaryNavFontColour', 'SecondaryNavFontColour') || DEFAULTS.secondaryNavFontColour,
        iconColour: pick(source, 'iconColour', 'IconColour') || DEFAULTS.iconColour,
        sidebarIconColour: pick(source, 'sidebarIconColour', 'SidebarIconColour') || DEFAULTS.sidebarIconColour,
        logoUrl: pick(source, 'logoUrl', 'LogoUrl', 'logo', 'Logo'),
        loadingLogoUrl: pick(source, 'loadingLogoUrl', 'LoadingLogoUrl', 'loadingLogo', 'LoadingLogo'),
        faviconUrl: pick(source, 'faviconUrl', 'FaviconUrl', 'favicon', 'Favicon'),
        logo: null,
        loadingLogo: null,
        favicon: null,
        logoFileName: null,
        loadingLogoFileName: null,
        faviconFileName: null,
    };
};

const ClientConfig = ({ clientId, clientName }) => {
    const [config, setConfig] = useState({
        ...DEFAULTS,
        logoUrl: null, loadingLogoUrl: null, faviconUrl: null,
        logo: null, loadingLogo: null, favicon: null,
        logoFileName: null, loadingLogoFileName: null, faviconFileName: null,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!clientId) return;
        const load = async () => {
            setIsLoading(true);
            try {
                const r = await getClientConfigAsync(clientId);
                if (r) {
                    setConfig(mapApiConfigToState(r));
                }
            } catch {
                showToast(false, 'Failed to load client configuration');
            }
            setIsLoading(false);
        };
        load();
    }, [clientId]);

    const handleColor = (e) => {
        setConfig(c => ({ ...c, [e.target.name]: e.target.value }));
    };

    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const name = e.target.name;
        setConfig(c => ({
            ...c,
            [name]: file,
            [`${name}Url`]: URL.createObjectURL(file),
            [`${name}FileName`]: file.name,
        }));
    };

    const handleSave = async () => {
        if (!clientId) return;
        setIsSaving(true);
        try {
            const form = new FormData();
            form.append('ClientId', clientId);
            form.append('PrimaryNavColour', config.primaryNavColour);
            form.append('SecondaryNavColour', config.secondaryNavColour);
            form.append('PrimaryButtonColour', config.primaryButtonColour);
            form.append('SecondaryButtonColour', config.secondaryButtonColour);
            form.append('PrimaryNavFontColour', config.primaryNavFontColour);
            form.append('SecondaryNavFontColour', config.secondaryNavFontColour);
            form.append('IconColour', config.iconColour);
            form.append('SidebarIconColour', config.sidebarIconColour);
            if (config.logo) form.append('Logo', config.logo);
            if (config.loadingLogo) form.append('LoadingLogo', config.loadingLogo);
            if (config.favicon) form.append('Favicon', config.favicon);

            const res = await updateClientConfigAsync(form);
            if (res && (res.success || res.message)) {
                showToast(true, 'Styling saved successfully');
            } else {
                showToast(false, 'Failed to save styling');
            }
        } catch {
            showToast(false, 'Failed to save styling');
        }
        setIsSaving(false);
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <span className="spinner-border text-primary" role="status" />
                <p className="mt-3 text-muted">Loading configuration...</p>
            </div>
        );
    }

    const c = config;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'start' }}>

            {/* ── Editor ── */}
            <div>
                <p style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', marginBottom: '14px' }}>Brand Assets</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
                    {[
                        { name: 'logo', id: 'cfgLogo', label: 'Sidebar Logo', accept: '.png,.jpeg,.jpg', urlKey: 'logoUrl', fileNameKey: 'logoFileName' },
                        { name: 'loadingLogo', id: 'cfgLoadingLogo', label: 'Loading Logo', accept: '.png,.jpeg,.jpg', urlKey: 'loadingLogoUrl', fileNameKey: 'loadingLogoFileName' },
                        { name: 'favicon', id: 'cfgFavicon', label: 'Favicon', accept: '.png,.ico', urlKey: 'faviconUrl', fileNameKey: 'faviconFileName' },
                    ].map(asset => (
                        <div key={asset.name}>
                            <label style={{ fontWeight: '600', fontSize: '0.82rem', color: '#475569', display: 'block', marginBottom: '6px' }}>
                                {asset.label}
                            </label>
                            <div style={{
                                height: '100px',
                                border: `2px dashed ${c[asset.urlKey] ? '#6366f1' : '#cbd5e1'}`,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '8px',
                                background: c[asset.urlKey] ? '#f5f3ff' : '#f8fafc',
                                overflow: 'hidden',
                            }}>
                                {c[asset.urlKey]
                                    ? <img src={c[asset.urlKey]} alt={asset.label} style={{ maxHeight: '88px', maxWidth: '100%', objectFit: 'contain', padding: '4px' }} />
                                    : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>No image</span>
                                }
                            </div>
                            <div className="custom-file">
                                <input
                                    value=""
                                    className="custom-file-input"
                                    accept={asset.accept}
                                    name={asset.name}
                                    id={asset.id}
                                    type="file"
                                    onChange={handleFile}
                                />
                                <label className="custom-file-label" htmlFor={asset.id} style={{ fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {c[asset.fileNameKey] || 'Choose file...'}
                                </label>
                            </div>
                        </div>
                    ))}
                </div>

                <p style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', marginBottom: '14px' }}>Colour Palette</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '32px' }}>
                    {COLOR_FIELDS.map(field => (
                        <div key={field.name} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                            <input
                                type="color"
                                name={field.name}
                                value={c[field.name] || '#000000'}
                                onChange={handleColor}
                                style={{ width: '44px', height: '44px', padding: '2px', cursor: 'pointer', border: '2px solid #e2e8f0', borderRadius: '8px', flexShrink: 0, background: 'none' }}
                            />
                            <div>
                                <div style={{ fontWeight: '600', fontSize: '0.82rem', color: '#1e293b' }}>{field.label}</div>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#64748b' }}>{c[field.name] || '#000000'}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        style={{
                            background: isSaving ? '#94a3b8' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '12px 32px',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: isSaving ? 'none' : '0 4px 14px rgba(102,126,234,0.35)',
                        }}
                    >
                        {isSaving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
                        {isSaving ? 'Saving...' : 'Save Styling'}
                    </button>
                </div>
            </div>

            {/* ── Live Preview ── */}
            <div style={{ position: 'sticky', top: '20px' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', marginBottom: '14px' }}>Live Preview</p>

                {/* Browser chrome */}
                <div style={{ background: '#dde1e7', borderRadius: '12px 12px 0 0', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {['#ff5f57', '#febc2e', '#28c841'].map(col => (
                        <div key={col} style={{ width: '11px', height: '11px', borderRadius: '50%', background: col, flexShrink: 0 }} />
                    ))}
                    <div style={{ flex: 1, margin: '0 8px', background: 'white', borderRadius: '5px', height: '20px', display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                        <span style={{ fontSize: '9px', color: '#94a3b8' }}>app.academyone.com</span>
                    </div>
                    {c.faviconUrl
                        ? <img src={c.faviconUrl} alt="favicon" style={{ width: '14px', height: '14px', objectFit: 'contain', flexShrink: 0 }} />
                        : <div style={{ width: '14px', height: '14px', borderRadius: '3px', background: c.iconColour, opacity: 0.6, flexShrink: 0 }} />
                    }
                </div>

                {/* App shell */}
                <div style={{ border: '1px solid #cbd5e1', borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', display: 'flex', height: '500px' }}>

                    {/* Sidebar */}
                    <div style={{
                        width: '90px',
                        backgroundColor: c.primaryNavColour,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '16px 0 16px',
                        gap: '4px',
                        flexShrink: 0,
                    }}>
                        {/* Logo area */}
                        <div style={{ width: '64px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                            {c.logoUrl
                                ? <img src={c.logoUrl} alt="sidebar logo" style={{ maxWidth: '60px', maxHeight: '52px', objectFit: 'contain' }} />
                                : <div style={{ width: '44px', height: '44px', background: c.sidebarIconColour, borderRadius: '10px', opacity: 0.3 }} />
                            }
                        </div>
                        {/* Nav items */}
                        {['Dashboard', 'Courses', 'Learners', 'Reports', 'Settings'].map((label, i) => (
                            <div key={i} style={{
                                width: '76px',
                                padding: '8px 0',
                                borderRadius: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                                background: i === 0 ? `${c.sidebarIconColour}28` : 'transparent',
                            }}>
                                <div style={{ width: '22px', height: '22px', borderRadius: '6px', background: c.sidebarIconColour, opacity: i === 0 ? 1 : 0.4 }} />
                                <span style={{ fontSize: '8px', color: c.sidebarIconColour, opacity: i === 0 ? 1 : 0.5, fontWeight: i === 0 ? 700 : 400 }}>{label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Main content */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: '#f1f5f9' }}>

                        {/* Top nav bar */}
                        <div style={{
                            height: '54px',
                            backgroundColor: c.secondaryNavColour,
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 18px',
                            gap: '10px',
                            flexShrink: 0,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        }}>
                            <span style={{ flex: 1, fontSize: '12px', fontWeight: 700, color: c.secondaryNavFontColour, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {clientName || 'Client Platform'}
                            </span>
                            <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: c.iconColour, opacity: 0.85, flexShrink: 0 }} />
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e2e8f0', flexShrink: 0 }} />
                        </div>

                        {/* Scrollable page body */}
                        <div style={{ flex: 1, padding: '18px 16px', overflowY: 'auto' }}>
                            {/* Page heading */}
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ height: '14px', width: '110px', background: '#1e293b', borderRadius: '4px', marginBottom: '6px' }} />
                                <div style={{ height: '9px', width: '170px', background: '#cbd5e1', borderRadius: '4px' }} />
                            </div>

                            {/* Stat cards */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '14px' }}>
                                {[c.primaryButtonColour, c.iconColour, c.secondaryButtonColour].map((bg, i) => (
                                    <div key={i} style={{ background: 'white', borderRadius: '10px', padding: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                                        <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: bg, marginBottom: '8px', opacity: i === 2 ? 0.5 : 1 }} />
                                        <div style={{ height: '7px', width: '38px', background: '#e2e8f0', borderRadius: '3px', marginBottom: '5px' }} />
                                        <div style={{ height: '13px', width: '26px', background: '#334155', borderRadius: '3px' }} />
                                    </div>
                                ))}
                            </div>

                            {/* Content card */}
                            <div style={{ background: 'white', borderRadius: '10px', padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '12px' }}>
                                <div style={{ height: '10px', width: '70px', background: '#334155', borderRadius: '3px', marginBottom: '12px' }} />
                                {[100, 85, 65, 45].map((w, i) => (
                                    <div key={i} style={{ height: '7px', width: `${w}%`, background: '#e2e8f0', borderRadius: '3px', marginBottom: '6px' }} />
                                ))}
                            </div>

                            {/* Buttons */}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <div style={{ padding: '7px 14px', background: c.primaryButtonColour, borderRadius: '7px', fontSize: '10px', color: c.primaryNavFontColour, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    Primary Action
                                </div>
                                <div style={{ padding: '7px 12px', background: c.secondaryButtonColour, borderRadius: '7px', fontSize: '10px', color: '#475569', fontWeight: 600, border: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                    Secondary
                                </div>
                                <div style={{ padding: '7px 12px', borderRadius: '7px', fontSize: '10px', color: c.iconColour, fontWeight: 600, border: `1.5px solid ${c.iconColour}`, background: 'transparent', whiteSpace: 'nowrap' }}>
                                    Outline
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Loading screen strip */}
                <div style={{ marginTop: '10px', background: c.primaryNavColour, borderRadius: '10px', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', minHeight: '60px' }}>
                    {c.loadingLogoUrl
                        ? <img src={c.loadingLogoUrl} alt="loading logo" style={{ maxHeight: '36px', maxWidth: '140px', objectFit: 'contain' }} />
                        : <div style={{ width: '90px', height: '16px', background: c.sidebarIconColour, borderRadius: '4px', opacity: 0.25 }} />
                    }
                    <span style={{ fontSize: '9px', color: c.sidebarIconColour, opacity: 0.6, whiteSpace: 'nowrap' }}>Loading screen</span>
                </div>

                <p style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '8px', textAlign: 'center' }}>
                    Updates live as you edit
                </p>
            </div>
        </div>
    );
};

export default ClientConfig;
